/**
 * WebSocket Message Handler Service
 * Handles different types of WebSocket messages and routes them to appropriate handlers
 */

import router from '@/router'
import store from '@/store'

class WebSocketMessageHandler {
  constructor() {
    this.messageHandlers = new Map()
    this.eventListeners = new Map()
    
    // Register default message handlers
    this.registerHandler('DW_ETL_JOB', this.handleDwEtlJob.bind(this))
    this.registerHandler('FORCE_LOGOUT', this.handleForceLogout.bind(this))
    // Add more message types here as needed
    // this.registerHandler('NOTIFICATION', this.handleNotification.bind(this))
    // this.registerHandler('USER_UPDATE', this.handleUserUpdate.bind(this))
  }

  /**
   * Register a message handler for a specific message type
   * @param {string} messageType - The type of message to handle
   * @param {function} handler - The handler function
   */
  registerHandler(messageType, handler) {
    this.messageHandlers.set(messageType, handler)
  }

  /**
   * Process incoming WebSocket message
   * @param {MessageEvent} event - WebSocket message event
   */
  processMessage(event) {
    try {
      const message = JSON.parse(event.data)
      
      if (!message.type) {
        console.warn('WebSocket message missing type field:', message)
        return
      }

      const handler = this.messageHandlers.get(message.type)
      
      if (handler) {
        handler(message)
      } else {
        console.warn(`No handler registered for message type: ${message.type}`)
      }

      // Emit generic message event for components that want to listen to all messages
      this.emit('message', { type: message.type, data: message })
      
    } catch (error) {
      console.error('Error processing WebSocket message:', error)
    }
  }

  /**
   * Handle DW_ETL_JOB messages
   * @param {Object} message - The message object
   */
  handleDwEtlJob(message) {
    const { status, progress, error, client_id, schema } = message

    // Create event data for components to listen to
    const eventData = {
      status,
      progress: progress || null,
      error: error || null,
      clientId: client_id,
      schema,
      isWorking: status === 'WORKING',
      isDone: status === 'DONE',
      hasError: status === 'DONE_WITH_ERROR'
    }

    // Emit specific ETL job event
    this.emit('dwEtlJobUpdate', eventData)
  }

  /**
   * Handle notification messages (placeholder for future implementation)
   * @param {Object} message - The message object
   */
  handleNotification(message) {
    this.emit('notification', message)
  }

  /**
   * Handle user update messages (placeholder for future implementation)
   * @param {Object} message - The message object
   */
  handleUserUpdate(message) {
    this.emit('userUpdate', message)
  }

  /**
   * Add event listener for specific message types or events
   * @param {string} event - Event name
   * @param {function} callback - Callback function
   */
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, [])
    }
    this.eventListeners.get(event).push(callback)
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {function} callback - Callback function to remove
   */
  off(event, callback) {
    if (this.eventListeners.has(event)) {
      const listeners = this.eventListeners.get(event)
      const index = listeners.indexOf(callback)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }

  /**
   * Emit event to all listeners
   * @param {string} event - Event name
   * @param {any} data - Event data
   */
  emit(event, data) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          console.error(`Error in ${event} listener:`, error)
        }
      })
    }
  }

  /**
   * Get current ETL job status (if available)
   * @returns {Object|null} Current ETL job status
   */
  getCurrentEtlStatus() {
    return this.currentEtlStatus || null
  }

  /**
   * Clear all event listeners
   */
  clearListeners() {
    this.eventListeners.clear()
  }

  /**
   * Handle FORCE_LOGOUT messages
   * @param {Object} message - The message object with type, reason_id, and description
   */
  handleForceLogout(message) {
    this.emit('forceLogout', { 
      type: message.type,
      description: message.description 
    })
    
    // Store the logout reason for the login page
    if (message.description) {
      sessionStorage.setItem('force_logout_reason', message.description)
    }
    
    // Clear authentication state using the proper store namespace
    store.commit('login/LOGOUT')
    
    // Redirect to login page
    router.push('/login').catch(() => {
      // Fallback if router push fails
      window.location.href = '/login'
    })
  }
}

// Create and export a single instance
const webSocketMessageHandler = new WebSocketMessageHandler()

export default webSocketMessageHandler 


// WEBPACK FOOTER //
// ./src/services/WebSocketMessageHandler.js